import React from 'react';

import ColoredChartText from '@components/report/chart/colored-chart-text/ColoredChartText';
import HorizontalRateChart, {
	HorizontalRateChartProps,
} from '@components/report/chart/horizontal-rate-chart/HorizontalRateChart';
import RadialBarChart, {
	RadialBarChartProps,
} from '@components/report/chart/radial-bar-chart/RadialBarChart';
import SymmetricVerticalBarChart, {
	SymmetricVerticalBarChartProps,
} from '@components/report/chart/symmetric-vertical-bar-chart/SymmetricVerticalBarChart';
import ReportCard from '@components/report/report-card/ReportCard';

import { PlatformCode } from 'src/type/common';

import { containerClass, gridClass, innerClass } from './style.css';

export interface AudienceProps {
	mainAudience?: string;
	mainAudiencePercentage?: number;
	genderBarChart?: SymmetricVerticalBarChartProps;
	// genderRadialChart?: RadialBarChartProps;
	languageRateChart?: HorizontalRateChartProps;
	platformCode?: PlatformCode;

	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const Audience = ({
	mainAudience,
	mainAudiencePercentage,
	platformCode,
	isLoading,
	isError,
	lackOfPermission,
	...props
}: AudienceProps) => {
	const status = { isLoading, isError, lackOfPermission };
	const available = !isLoading && !isError && !lackOfPermission;

	const fiveAgeSections = ['18-24', '25-34', '35-44', '45-54', '55+'];
	const sixAgeSections = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];
	const FIVE_DATA_LENGTH = 5;
	const SIX_DATA_LENGTH = 6;
	const sixAgeSectionPlatform = ['yt', 'tt'];

	const dataItem = { valuePercentage: 0 };
	const dataArray = sixAgeSectionPlatform.includes(platformCode ?? '')
		? new Array(SIX_DATA_LENGTH).fill(dataItem)
		: new Array(FIVE_DATA_LENGTH).fill(dataItem);

	const genderBarChartBase: SymmetricVerticalBarChartProps = {
		topDataLabel: '여성',
		bottomDataLabel: '남성',
		categories: sixAgeSectionPlatform.includes(platformCode ?? '')
			? sixAgeSections
			: fiveAgeSections,
		topData: dataArray,
		bottomData: dataArray,
	};

	const genderRadialChartBase: RadialBarChartProps = {
		title: mainAudience ?? '-',
		isPositive: true,
		value: mainAudiencePercentage ?? 0,
	};

	const languageRateChartBase: HorizontalRateChartProps = {
		desc: (
			<>
				계정의 주요 오디언스는 주로 <span key="desc"> - </span>를 사용합니다.
			</>
		),
		data: [
			{
				label: '한국어',
				value: '-%',
				valuePercentage: 0,
			},
			{
				label: '영어',
				value: '-%',
				valuePercentage: 0,
			},
			{
				label: '일본어',
				value: '-%',
				valuePercentage: 0,
			},
			{
				label: '기타',
				value: '-%',
				valuePercentage: 0,
			},
		],
	};

	const genderBarChart = available ? props.genderBarChart : genderBarChartBase;
	const languageRateChart = available ? props.languageRateChart : languageRateChartBase;

	return (
		<div className={gridClass}>
			<ReportCard
				label={'오디언스 인구 비율'}
				tooltip={
					'계정의 주요 오디언스가 성별과 연령별로 어떻게 구성되어 있는지와 전체 오디언스 대비 주요 오디언스의 비율을 파악할 수 있습니다.'
				}
				align="horizontal"
				lackOfPermission={lackOfPermission}
			>
				<div className={containerClass}>
					<ColoredChartText {...status}>
						계정의 주요 오디언스는{' '}
						{
							<span key="main">
								{mainAudience ? `${mainAudience}(${mainAudiencePercentage}%)` : ' - '}
							</span>
						}
						입니다.
					</ColoredChartText>

					<div className={innerClass}>
						<SymmetricVerticalBarChart
							barWidth={sixAgeSectionPlatform.includes(platformCode ?? '') ? 46 : 58}
							{...genderBarChartBase}
							{...genderBarChart}
							{...status}
						/>
						<RadialBarChart {...genderRadialChartBase} {...status} />
					</div>
				</div>
			</ReportCard>
			<ReportCard
				label={'오디언스 언어 비율'}
				tooltip={
					'계정의 오디언스가 사용하는 주요 언어의 분포를 나타냅니다. 오디언스의 언어 사용 현황을 이해하고, 특정 언어를 사용하는 그룹을 대상으로 효과적인 마케팅 전략을 수립할 수 있습니다.'
				}
				lackOfPermission={lackOfPermission}
			>
				<HorizontalRateChart
					{...languageRateChartBase}
					{...languageRateChart}
					{...status}
				/>
			</ReportCard>
		</div>
	);
};

export default Audience;
